/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/* eslint-disable no-unused-vars */

/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import i18n from "i18n";
import { createContext, ReactNode, useContext, useEffect, useMemo, useReducer } from "react";

// The Material Dashboard 2 PRO React TSUI Dashboard PRO Material main context
const MaterialUI = createContext<any>(null);

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

/**
 * Allowed color variants for the sidebar navigation.
 */
type SidenavColor =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "light"
  | "dark";

/**
 * Text direction for internationalization support.
 * - ltr: Left-to-right (English, Spanish, French, etc.)
 * - rtl: Right-to-left (Arabic, Hebrew, etc.)
 */
type NavbarDirection = "ltr" | "rtl";

/**
 * Layout mode for the application.
 * - dashboard: Full layout with sidebar navigation
 * - page: Simplified layout without sidebar (for login, public pages)
 */
type LayoutType = "dashboard" | "page";

/**
 * Supported languages for internationalization.
 * - en: English
 * - es: Spanish
 * - zh: Chinese
 * - fr: French
 * - it: Italian
 * - pt: Portuguese
 */
type LanguageType = "en" | "es" | "zh" | "fr" | "it" | "pt";

/**
 * Global UI theme state for the application.
 * Manages sidebar, navbar, theme mode, language, and layout configuration.
 */
interface StateTypes {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  whiteSidenav: boolean;
  sidenavColor: SidenavColor;
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: NavbarDirection;
  layout: LayoutType;
  darkMode: boolean;
  language?: LanguageType;
}

/**
 * Redux-style action type for theme state updates.
 * Dispatched actions trigger state changes via the reducer function.
 */
interface ActionTypes {
  type:
    | "MINI_SIDENAV"
    | "TRANSPARENT_SIDENAV"
    | "WHITE_SIDENAV"
    | "SIDENAV_COLOR"
    | "TRANSPARENT_NAVBAR"
    | "FIXED_NAVBAR"
    | "OPEN_CONFIGURATOR"
    | "DIRECTION"
    | "LAYOUT"
    | "DARKMODE"
    | "LANGUAGE";
  value: any;
}

/**
 * Theme state reducer for managing UI configuration changes.
 * 
 * Handles actions:
 * - MINI_SIDENAV: Toggle sidebar minimized state
 * - TRANSPARENT_SIDENAV: Toggle sidebar transparency
 * - WHITE_SIDENAV: Toggle white sidebar background
 * - SIDENAV_COLOR: Change sidebar color theme
 * - TRANSPARENT_NAVBAR: Toggle navbar transparency
 * - FIXED_NAVBAR: Toggle fixed navbar position
 * - OPEN_CONFIGURATOR: Toggle theme configurator panel
 * - DIRECTION: Change text direction (ltr/rtl)
 * - LAYOUT: Change layout mode (dashboard/page)
 * - DARKMODE: Toggle dark mode theme
 * - LANGUAGE: Change application language
 * 
 * @param state Current theme state
 * @param action Action with type and value
 * @returns Updated state object
 * @throws Error if action type is not recognized
 */
function reducer(state: StateTypes, action: ActionTypes) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "LANGUAGE": {
      return { ...state, language: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * React Context Provider for global theme and UI configuration.
 * 
 * Provides centralized state management for:
 * - Sidebar configuration (size, color, transparency)
 * - Navbar settings (fixed position, transparency)
 * - Theme mode (light/dark)
 * - Text direction (LTR/RTL)
 * - Language selection
 * - Layout mode (dashboard/page)
 * 
 * Initial state includes default configurations for a dashboard layout
 * with light theme and English language.
 * 
 * When language changes, automatically updates i18next instance to
 * reflect new translations throughout the application.
 * 
 * @param children React components to wrap with theme context
 * @returns Context Provider component
 */
function MaterialUIControllerProvider({ children }: { children: ReactNode }): JSX.Element {
  const initialState: StateTypes = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    language: i18n.language as LanguageType,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  useEffect(() => {
    i18n.changeLanguage(controller.language);
  }, [controller.language]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

/**
 * Custom hook to access Material UI theme controller state and dispatch.
 * 
 * Returns a tuple containing:
 * - [0]: Current theme state (StateTypes)
 * - [1]: Dispatch function for state updates
 * 
 * Must be used within MaterialUIControllerProvider component tree.
 * 
 * @returns [controller, dispatch] tuple for theme management
 * @throws Error if used outside MaterialUIControllerProvider
 * 
 * @example
 * const [controller, dispatch] = useMaterialUIController();
 * const { darkMode, miniSidenav } = controller;
 * setDarkMode(dispatch, true);
 */
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

/**
 * Toggle sidebar minimized state.
 * @param dispatch Theme dispatch function
 * @param value True to minimize sidebar, false to expand
 */
const setMiniSidenav = (
  dispatch: (arg: { type: "MINI_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "MINI_SIDENAV", value });

/**
 * Toggle sidebar transparency.
 * @param dispatch Theme dispatch function
 * @param value True for transparent sidebar, false for solid background
 */
const setTransparentSidenav = (
  dispatch: (arg: { type: "TRANSPARENT_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "TRANSPARENT_SIDENAV", value });

/**
 * Toggle white sidebar background.
 * @param dispatch Theme dispatch function
 * @param value True for white sidebar, false for default color
 */
const setWhiteSidenav = (
  dispatch: (arg: { type: "WHITE_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "WHITE_SIDENAV", value });

/**
 * Set sidebar color theme.
 * @param dispatch Theme dispatch function
 * @param value Color variant (primary, secondary, info, success, warning, error, light, dark)
 */
const setSidenavColor = (
  dispatch: (arg: {
    type: "SIDENAV_COLOR";
    value: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
  }) => void,
  value: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark"
) => dispatch({ type: "SIDENAV_COLOR", value });

/**
 * Toggle navbar transparency.
 * @param dispatch Theme dispatch function
 * @param value True for transparent navbar, false for solid background
 */
const setTransparentNavbar = (
  dispatch: (arg: { type: "TRANSPARENT_NAVBAR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "TRANSPARENT_NAVBAR", value });

/**
 * Toggle fixed navbar position.
 * @param dispatch Theme dispatch function
 * @param value True for fixed navbar, false for scrollable
 */
const setFixedNavbar = (
  dispatch: (arg: { type: "FIXED_NAVBAR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "FIXED_NAVBAR", value });

/**
 * Toggle theme configurator panel visibility.
 * @param dispatch Theme dispatch function
 * @param value True to open configurator, false to close
 */
const setOpenConfigurator = (
  dispatch: (arg: { type: "OPEN_CONFIGURATOR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "OPEN_CONFIGURATOR", value });

/**
 * Set text direction for internationalization.
 * @param dispatch Theme dispatch function
 * @param value 'ltr' for left-to-right, 'rtl' for right-to-left
 */
const setDirection = (
  dispatch: (arg: { type: "DIRECTION"; value: "ltr" | "rtl" }) => void,
  value: "ltr" | "rtl"
) => dispatch({ type: "DIRECTION", value });

/**
 * Set application layout mode.
 * @param dispatch Theme dispatch function
 * @param value 'dashboard' for full layout with sidebar, 'page' for simple layout
 */
const setLayout = (
  dispatch: (arg: { type: "LAYOUT"; value: "dashboard" | "page" }) => void,
  value: "dashboard" | "page"
) => dispatch({ type: "LAYOUT", value });

/**
 * Toggle dark mode theme.
 * @param dispatch Theme dispatch function
 * @param value True for dark mode, false for light mode
 */
const setDarkMode = (
  dispatch: (arg: { type: "DARKMODE"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "DARKMODE", value });

/**
 * Set application language for internationalization.
 * Automatically updates i18next instance when changed.
 * @param dispatch Theme dispatch function
 * @param value Language code (en, es, zh, fr, it, pt)
 */
const setLanguage = (
  dispatch: (arg: { type: "LANGUAGE"; value: "en" | "es" | "zh" | "fr" | "it" | "pt" }) => void,
  value: "en" | "es" | "zh" | "fr" | "it" | "pt"
) => dispatch({ type: "LANGUAGE", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setLanguage,
};
